import * as amplitude from "@amplitude/analytics-react-native"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import { authService } from "@data/services/authService"
import { hasRole } from "@data/utility/ability"
import React, { useEffect, useRef, useState } from "react"
import { Pressable, View } from "react-native"
import { useDispatch, useSelector, useStore } from "react-redux"
import MaterialContentDisplay from "../../../../common/components/material/MaterialContentDisplay"
import { adaptedLineWidth } from "../../../../design-system/molecules/RichText"
import ReadingSheet from "./ReadingSheet"
import {
    isCustomReading,
    readingChoice,
    sameReading,
} from "@data/utility/readingPresets"

function saveIfChanged(dispatch, draft, reading) {
    if (draft == null || sameReading(draft, reading)) return
    dispatch(
        apiRequest(authService.updateReadingSettings, {}, readingChoice(draft))
    )
}

/**
 * L'énoncé texte tel que l'élève le lit : sa lecture (fiche « Ma lecture »), la lecture adaptée
 * allumée d'office. L'énoncé se lit toujours en entier, ses questions à la suite.
 *
 * Seul l'élève, sur son propre compte, a une lecture : le tuteur, le relecteur et le parent (le
 * mode parent passe par le compte de l'élève) voient la feuille standard, comme avant.
 */
export default function EnonceReader(props) {
    const { material, sheetStyle } = props
    const user = useSelector((state) => state.data.currentUser?.object)
    const isParentMode = useSelector((state) => state.data.family?.isParentMode)
    const reading = user?.profile?.reading
    const readsForThemself =
        hasRole(user, "student") && !isParentMode && reading != null

    if (!readsForThemself) {
        return (
            <View style={sheetStyle}>
                <MaterialContentDisplay material={material} />
            </View>
        )
    }

    // La clé remonte le lecteur quand l'énoncé change : le réglage laissé fiche ouverte part au
    // démontage, et la fiche repart fermée.
    return (
        <StudentEnonce
            key={material?.id}
            material={material}
            sheetStyle={sheetStyle}
            reading={reading}
        />
    )
}

function StudentEnonce({ material, sheetStyle, reading }) {
    const dispatch = useDispatch()
    const [sheetOpen, setSheetOpen] = useState(false)
    // Fiche ouverte, l'énoncé suit les gestes de l'élève sans rien envoyer : le réglage part une
    // fois, à la fermeture. Une requête par geste croisait ses réponses, et la taille revenait en
    // arrière le temps qu'arrive la dernière.
    const [draft, setDraft] = useState(null)
    const shown = draft ?? reading

    // En lecture adaptée, des lignes d'environ 65 caractères : sur grand écran, toute la page de
    // l'énoncé se resserre au centre ; sur un téléphone, elle est de toute façon plus étroite.
    const column = shown.adapted
        ? {
              width: "100%",
              maxWidth: adaptedLineWidth(shown) + sheetChrome(sheetStyle),
              alignSelf: "center",
          }
        : null

    const changeReading = (next) => {
        setDraft(next)
        amplitude.track("reading.settings.changed", readingChoice(next))
    }

    const closeSheet = () => {
        setSheetOpen(false)
        saveIfChanged(dispatch, draft, reading)
        setDraft(null)
    }

    // Un réglage fait fiche ouverte part aussi quand l'énoncé s'en va avant qu'elle ne se ferme
    // (un autre énoncé poussé en live, la fenêtre refermée) : l'élève l'a vu appliqué, il le
    // retrouve au suivant. Sauf si c'est une bascule de compte qui l'emporte : la requête partirait
    // sous le jeton de l'autre enfant, et lui écrirait ce réglage.
    const store = useStore()
    const token = useSelector((state) => state.data.session?.object?.token)
    const unsaved = useRef(null)
    unsaved.current = { draft, reading, token }
    useEffect(
        () => () => {
            const {
                draft: left,
                reading: saved,
                token: owner,
            } = unsaved.current
            if (store.getState().data.session?.object?.token !== owner) return
            saveIfChanged(dispatch, left, saved)
        },
        [dispatch, store]
    )

    const openSheet = () => {
        setSheetOpen(true)
        amplitude.track("reading.sheet.opened", { adapted: reading.adapted })
    }

    return (
        <View style={column}>
            <View style={sheetStyle}>
                <MaterialContentDisplay material={material} reading={shown} />
                <Pressable
                    onPress={openSheet}
                    style={styles.readingIcon}
                    accessibilityRole="button"
                    accessibilityLabel="Ma lecture : régler l'affichage de l'énoncé"
                    hitSlop={8}
                >
                    <TextM
                        fontWeight="ExtraBold"
                        fontSize={15}
                        color={
                            isCustomReading(shown)
                                ? theme.colors.primary
                                : theme.colors.light
                        }
                    >
                        Aa
                    </TextM>
                </Pressable>
            </View>

            <ReadingSheet
                visible={sheetOpen}
                reading={shown}
                onChange={changeReading}
                onClose={closeSheet}
            />
        </View>
    )
}

/** Ce que la feuille ajoute autour du texte, de part et d'autre : lu sur son style. */
function sheetChrome(style) {
    return (
        2 *
        ((style?.padding ?? 0) +
            (style?.borderWidth ?? 0) +
            (style?.margin ?? 0))
    )
}

const styles = {
    readingIcon: {
        alignSelf: "flex-end",
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
}
